import { useCallback, useRef, useState } from 'react'
import { ApiError } from './api.ts'
import { messageOf } from './queries.ts'

/**
 * ══ CHẠY MỘT LỆNH GHI TỪ HỘP THOẠI — trả KẾT QUẢ, có cờ BẬN (soát 16/10, T39 · L30 · L35 · T1) ══════
 *
 * Trước đây mỗi màn tự viết một `run()` (hơn 70 bản), cùng ba lỗi:
 *  · `run().then(close)` — `run` nuốt lỗi rồi trả `void`, nên `then` chạy CẢ KHI máy chủ từ chối: Gia
 *    hạn HĐ trả 400 mà hộp vẫn đóng, nhập số HĐ trùng mà khối thu về như đã lưu (T39);
 *  · không có cờ bận: nhấp đúp Lưu gửi HAI lệnh — bản ghi tạo xong rồi màn báo đỏ "đã có" (L30, L35).
 *    Bản cũ chặn bằng `submitOnce` (`lib/util.ts:47`, 800 ms); ở đây chặn đúng lúc lệnh còn bay, chuẩn hơn
 *    một khoảng thời gian cứng;
 *  · câu lỗi đặt vào băng đầu trang, bị lớp phủ của hộp che (T1) — nay trả `err` để màn đưa vào
 *    `<Modal error>` khi hộp đang mở.
 *
 * Màn nào cũng chỉ cần: `const action = useAction()`, gọi `const r = await action.run(fn)` và CHỈ đóng
 * hộp khi `r.ok`. Xung đột phiên bản (409 có `currentVersion`) đi lối riêng `onConflict` để màn mở
 * `ConflictModal` thay vì hiện câu đỏ.
 */
/** `error`: câu lỗi đã đặt vào `err` (để hộp CON tự hiện khi `run` là của màn cha); `null` khi lệnh bị
 *  chặn vì đang bận hoặc đi lối xung đột. */
export type ActionResult<T> = { ok: true; value: T } | { ok: false; error: string | null }

/** Kiểu `run` mà màn cha truyền xuống hộp con (VD hồ sơ hợp đồng ▸ Gia hạn): hộp con đọc `ok` để biết
 *  có được đóng không, đọc `error` để tự hiện câu lỗi trong hộp của nó. */
export type RunFn = (fn: () => Promise<unknown>) => Promise<ActionResult<unknown>>

export interface RunOptions {
  /** 409 kèm `currentVersion` — màn mở `ConflictModal`; không đặt câu đỏ. */
  onConflict?: (e: ApiError) => void
}

export interface ActionState {
  run: <T>(fn: () => Promise<T>, opts?: RunOptions) => Promise<ActionResult<T>>
  /** Đang chờ máy chủ — gắn vào `disabled` của nút Lưu/Xác nhận. */
  busy: boolean
  err: string | null
  setErr: (m: string | null) => void
}

export const useAction = (): ActionState => {
  const [busy, setBusy] = useState(false)
  const [err, setErr] = useState<string | null>(null)
  // Cờ thật nằm ở ref: hai cú bấm trong CÙNG một nhịp vẽ đều thấy `busy` cũ là `false`.
  const flying = useRef(false)
  const run = useCallback(async <T,>(fn: () => Promise<T>, opts?: RunOptions): Promise<ActionResult<T>> => {
    if (flying.current) return { ok: false, error: null }
    flying.current = true
    setBusy(true)
    setErr(null)
    try {
      return { ok: true, value: await fn() }
    } catch (e) {
      if (opts?.onConflict !== undefined && e instanceof ApiError && e.currentVersion !== null) {
        opts.onConflict(e)
        return { ok: false, error: null }
      }
      const message = messageOf(e)
      setErr(message)
      return { ok: false, error: message }
    } finally {
      flying.current = false
      setBusy(false)
    }
  }, [])
  return { run, busy, err, setErr }
}
